import { createId } from "@paralleldrive/cuid2";
import {
  integer,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { z } from "zod";
import { createdAt, deletedAt, updatedAt } from "../lib/types";
import { cuid, dateTime, string } from "../validator/common";
import { tags } from "./tag";
import { trainingPrograms } from "./training-programs";
import { users } from "./users";

export const essayCategories = [
  "general",
  "powerlifting",
  "bodybuilding",
  "nutrition",
  "recovery",
  "science",
] as const;

export type EssayCategory = (typeof essayCategories)[number];

export const essayCalculatorTypes = [
  "ipf_gl",
  "one_rm",
  "wilks",
  "ffmi",
  "calorie",
  "water",
  "body_fat",
] as const;

export const essayCalculatorTypeSchema = z.enum(essayCalculatorTypes);
export type EssayCalculatorType = z.infer<typeof essayCalculatorTypeSchema>;

export const essayCategoryLabels: Record<
  EssayCategory,
  { label: string; description: string }
> = {
  general: {
    label: "Genel",
    description: "Genel fitness ve antrenman rehberleri",
  },
  powerlifting: {
    label: "Powerlifting",
    description: "Squat, bench press, deadlift ve güç odaklı yazılar",
  },
  bodybuilding: {
    label: "Vücut Geliştirme",
    description: "Hipertrofi, kas gelişimi ve hacim programlaması",
  },
  nutrition: {
    label: "Beslenme",
    description: "Kalori, makro besinler ve takviyeler",
  },
  recovery: {
    label: "Toparlanma & Sağlık",
    description: "Uyku, sakatlık önleme ve toparlanma",
  },
  science: {
    label: "Bilim & Araştırma",
    description: "Spor bilimleri ve güncel araştırmalar",
  },
};

// Relaxed schema to support both Lexical and Tiptap JSON content
export const lexicalContentSchema = z.any();

export type LexicalContent = z.infer<typeof lexicalContentSchema>;

export const essays = sqliteTable(
  "essays",
  {
    essayId: text("essay_id")
      .primaryKey()
      .notNull()
      .$defaultFn(() => createId()),
    title: text("title").notNull(),
    linkedProgramId: text("linked_program_id").references(
      () => trainingPrograms.programId,
      { onDelete: "set null" },
    ),
    linkedCalculator: text("linked_calculator", {
      enum: essayCalculatorTypes,
    }),
    ctaLabel: text("cta_label"),
    content: text("content", { mode: "json" })
      .$type<LexicalContent>()
      .notNull(),
    excerpt: text("excerpt"),
    authorId: text("author_id")
      .notNull()
      .references(() => users.id),
    category: text("category").default("general").notNull(),
    tags: text("tags", { mode: "json" })
      .$type<string[]>()
      .default([])
      .notNull(),
    tagId: text("tag_id").references(() => tags.tagId),
    coverImageUrl: text("cover_image_url"),
    metaDescription: text("meta_description"),
    ogImageUrl: text("og_image_url"),
    isPublished: integer("is_published", { mode: "boolean" })
      .default(false)
      .notNull(),
    publishedAt: integer("published_at", { mode: "timestamp" }),
    readTime: text("read_time"),
    createdAt,
    updatedAt,
    deletedAt,
  },
  (t) => [uniqueIndex("unique_essay_id").on(t.essayId)],
);

export const selectEssaySchema = z.object({
  essayId: cuid,
  linkedProgramId: cuid.nullable().optional(),
  linkedCalculator: essayCalculatorTypeSchema.nullable().optional(),
  ctaLabel: string.max(80).nullable().optional(),
  title: string.min(3).max(200),
  content: lexicalContentSchema,
  excerpt: string.max(200).nullable(),
  authorId: cuid,
  category: z.enum(essayCategories).default("general"),
  tags: z.array(z.string()).default([]),
  tagId: cuid.nullable(),
  coverImageUrl: string.nullable(),
  metaDescription: string.max(160).nullable(),
  ogImageUrl: string.nullable(),
  isPublished: z.boolean(),
  publishedAt: dateTime.nullable(),
  readTime: string.nullable(),
  createdAt: dateTime,
  updatedAt: dateTime.nullable(),
  deletedAt: dateTime.nullable(),
});

export const insertEssaySchema = z.object({
  linkedProgramId: cuid.nullable().optional(),
  linkedCalculator: essayCalculatorTypeSchema.nullable().optional(),
  ctaLabel: string.max(80).nullable().optional(),
  title: string.min(3, "Title must be at least 3 characters").max(200),
  content: lexicalContentSchema,
  category: z.enum(essayCategories).default("general").optional(),
  tags: z.array(z.string()).default([]).optional(),
  tagId: cuid.nullable().optional(),
  coverImageUrl: string.url("Must be a valid URL").nullable().optional(),
  metaDescription: string
    .max(160, "Meta description must be 160 characters or less")
    .nullable()
    .optional(),
  ogImageUrl: string.url("Must be a valid URL").nullable().optional(),
  isPublished: z.boolean().default(false).optional(),
});

export const patchEssaySchema = z.object({
  essayId: cuid,
  linkedProgramId: cuid.nullable().optional(),
  linkedCalculator: essayCalculatorTypeSchema.nullable().optional(),
  ctaLabel: string.max(80).nullable().optional(),
  title: string.min(3).max(200).optional(),
  content: lexicalContentSchema.optional(),
  category: z.enum(essayCategories).optional(),
  tags: z.array(z.string()).optional(),
  tagId: cuid.nullable().optional(),
  coverImageUrl: string.url().nullable().optional(),
  metaDescription: string.max(160).nullable().optional(),
  ogImageUrl: string.url().nullable().optional(),
  isPublished: z.boolean().optional(),
});

export type SelectEssay = z.infer<typeof selectEssaySchema>;
export type InsertEssay = z.infer<typeof insertEssaySchema>;
export type PatchEssay = z.infer<typeof patchEssaySchema>;
